import { useOutletContext, useSearchParams, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  Calendar,
  Database,
  FileText,
  Play,
  CheckCircle2,
  ChevronRight,
  Download,
  Server,
  HardDrive,
} from "lucide-react";
import {
  useEccAgenda,
  useEccSubirAgenda,
  useEccCdrPreview,
  useEccFollowup,
  useEccRun,
  useEccSrps,
  useEccStartRun,
} from "@/hooks/useEcc";
import { cn } from "@/lib/utils";
import { toLocalIsoDate } from "@/lib/localDate";
import { ObservationsBlock } from "@/components/ecc/ObservationsBlock";
import { FollowupPanel } from "@/components/ecc/FollowupPanel";
import type { EccArea, EccObservations, EccSrpSummary } from "@/api/types";

type Step = 1 | 2 | 3 | 4 | 5;

const STEPS = [
  { n: 1, label: "Periodo", icon: Calendar },
  { n: 2, label: "CDR", icon: Database },
  { n: 3, label: "SRP", icon: FileText },
  { n: 4, label: "Ejecución", icon: Play },
  { n: 5, label: "Resultado", icon: CheckCircle2 },
];

const SHORTCUTS = [
  { label: "Hoy", days: 0 },
  { label: "Ayer", days: 1 },
  { label: "Últimos 7 días", days: 7 },
  { label: "Últimos 15 días", days: 15 },
  { label: "Últimos 30 días", days: 30 },
];

function toIsoDate(d: Date) {
  // Fecha LOCAL (no UTC). Ver lib/localDate: toISOString() rodaría a mañana
  // después de las 18:00 CST y "Hoy" consultaría un día sin llamadas.
  return toLocalIsoDate(d);
}

function todayIso() {
  return toIsoDate(new Date());
}

function daysAgoIso(n: number) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toIsoDate(d);
}

function StepIndicator({ current }: { current: Step }) {
  return (
    <div className="flex items-center gap-1 mb-6">
      {STEPS.map((s, i) => {
        const done = s.n < current;
        const active = s.n === current;
        return (
          <div key={s.n} className="flex items-center gap-1">
            <div
              className={cn(
                "flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors",
                done
                  ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
                  : active
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground",
              )}
            >
              <s.icon className="size-3" />
              {s.label}
            </div>
            {i < STEPS.length - 1 && (
              <ChevronRight className="size-3 text-muted-foreground" />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Step 1: Periodo ─────────────────────────────────────────────────────────

function Step1Period({
  start,
  end,
  onApply,
}: {
  start: string;
  end: string;
  onApply: (s: string, e: string) => void;
}) {
  const [s, setS] = useState(start);
  const [e, setE] = useState(end);

  function applyShortcut(days: number) {
    if (days === 0) {
      const t = todayIso();
      setS(t);
      setE(t);
      onApply(t, t);
    } else if (days === 1) {
      const y = daysAgoIso(1);
      setS(y);
      setE(y);
      onApply(y, y);
    } else {
      const from = daysAgoIso(days);
      const to = daysAgoIso(1);
      setS(from);
      setE(to);
      onApply(from, to);
    }
  }

  return (
    <div className="space-y-5">
      <p className="text-sm text-muted-foreground">
        Selecciona el rango de fechas para extraer el CDR y las grabaciones.
        Las grabaciones de <strong>hoy</strong> se obtienen de Asterisk (192.168.0.1);
        las de días anteriores, de media1.tilatina.com.
      </p>

      <div className="flex flex-wrap gap-2">
        {SHORTCUTS.map((sh) => (
          <button
            key={sh.label}
            type="button"
            onClick={() => applyShortcut(sh.days)}
            className="rounded-full border px-3 py-1 text-xs font-medium transition-colors hover:bg-accent"
          >
            {sh.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">
            Desde
          </label>
          <input
            type="date"
            value={s}
            max={todayIso()}
            onChange={(ev) => setS(ev.target.value)}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">
            Hasta
          </label>
          <input
            type="date"
            value={e}
            min={s}
            max={todayIso()}
            onChange={(ev) => setE(ev.target.value)}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          disabled={!s || !e || s > e}
          onClick={() => onApply(s, e)}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
        >
          Siguiente: ver CDR
        </button>
      </div>
    </div>
  );
}

// ── Step 2: CDR preview ──────────────────────────────────────────────────────

function Step2Cdr({
  area,
  periodStart,
  periodEnd,
  onNext,
  onBack,
}: {
  area: EccArea;
  periodStart: string;
  periodEnd: string;
  onNext: () => void;
  onBack: () => void;
}) {
  const { data, isPending, error } = useEccCdrPreview(
    area,
    periodStart,
    periodEnd,
    true,
  );

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Resumen de llamadas en el periodo{" "}
        <strong>{periodStart}</strong> → <strong>{periodEnd}</strong> para el área{" "}
        <strong>{area.toUpperCase()}</strong>.
      </p>

      {isPending && (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-10 animate-pulse rounded border bg-muted" />
          ))}
        </div>
      )}

      {error && (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-400">
          No se pudo obtener el CDR (backend ECC no activo). Puedes continuar de todas formas.
        </div>
      )}

      {data && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-lg border bg-card p-3 text-center">
              <div className="text-2xl font-semibold">{data.total_calls}</div>
              <div className="text-xs text-muted-foreground">Total llamadas</div>
            </div>
            <div className="rounded-lg border bg-card p-3 text-center">
              <div className="text-2xl font-semibold">{data.answered_calls}</div>
              <div className="text-xs text-muted-foreground">Contestadas</div>
            </div>
            <div className="rounded-lg border bg-card p-3 text-center">
              <div className="text-2xl font-semibold">{data.recordings_available}</div>
              <div className="text-xs text-muted-foreground">Con grabación</div>
            </div>
          </div>

          <div className="rounded-lg border bg-card p-3 space-y-2">
            <div className="text-xs font-medium text-muted-foreground">Fuente de grabaciones</div>
            <div className="flex gap-4">
              <div className="flex items-center gap-2 text-sm">
                <Server className="size-4 text-amber-500" />
                <span>Asterisk (hoy):</span>
                <span className="font-medium">{data.source_breakdown.asterisk}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <HardDrive className="size-4 text-blue-500" />
                <span>media1 (anteriores):</span>
                <span className="font-medium">{data.source_breakdown.media1}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-between">
        <button
          type="button"
          onClick={onBack}
          className="rounded-md border px-4 py-2 text-sm font-medium"
        >
          Atrás
        </button>
        <button
          type="button"
          disabled={isPending}
          onClick={onNext}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
        >
          Siguiente: elegir SRP
        </button>
      </div>
    </div>
  );
}

// ── Step 3: Selección de SRP ─────────────────────────────────────────────────

function _srpToDefaultObservations(srp: EccSrpSummary): EccObservations {
  // Un SRP de solo-transcripción no tiene criterios que analizar: arranca con
  // observaciones vacías (el pipeline las ignora en modo transcripción).
  if (srp.transcription_only) {
    return { presets: [], custom: [] };
  }
  return {
    presets: (srp.default_observations ?? []).map((p) => ({
      code: p.code,
      label: p.label,
      checked: true,
    })),
    custom: [],
  };
}

/** Pregunta de agenda: los SRPs que nombran personas no corren a ciegas.
 *
 * Este análisis señala contactos por no contestar. Si la tabla de escalación
 * cambió y nadie actualizó la agenda, el reporte acusa a quien ya no trabaja
 * ahí y calla a quien sí debía contestar. Por eso se pregunta antes de correr
 * y un "sí hubo cambios" bloquea hasta que la agenda se actualice.
 */
function AgendaCheck({
  cliente,
  valor,
  onChange,
}: {
  cliente: string | null;
  valor: boolean | null;
  onChange: (v: boolean) => void;
}) {
  const { data: agenda } = useEccAgenda(cliente);
  const subir = useEccSubirAgenda(cliente);
  const [archivo, setArchivo] = useState<File | null>(null);
  const actualizada = agenda?.actualizada_en
    ? new Date(agenda.actualizada_en).toLocaleDateString("es-MX")
    : null;

  function handleSubir() {
    if (!archivo) return;
    // El archivo se lee aquí y se manda como texto: el servidor lo parsea, para
    // que las reglas (teléfono a 10 dígitos, alias de encabezados, filas que se
    // omiten) vivan en un solo lugar.
    const lector = new FileReader();
    lector.onload = () => {
      subir.mutate(String(lector.result ?? ""), {
        // Con la agenda ya actualizada, la corrida puede seguir.
        onSuccess: () => onChange(true),
      });
    };
    lector.readAsText(archivo);
  }

  return (
    <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3">
      <div className="text-sm font-medium">
        ¿Hubo cambios en la tabla de escalación?
      </div>
      <div className="mt-1 text-xs text-muted-foreground">
        {agenda
          ? `La agenda cargada tiene ${agenda.total} contactos de ${agenda.agencias} agencias` +
            (actualizada ? `, actualizada el ${actualizada}.` : ".")
          : "Cargando la agenda vigente…"}{" "}
        Este reporte nombra a las personas y señala a quien no contesta: con la
        agenda vencida, señala a quien ya no corresponde.
      </div>
      <div className="mt-3 space-y-2">
        <label className="flex cursor-pointer items-start gap-2 text-sm">
          <input
            type="radio"
            name="agenda"
            className="mt-0.5"
            checked={valor === true}
            onChange={() => onChange(true)}
          />
          <span>
            <span className="font-medium">No, sigue vigente.</span>{" "}
            <span className="text-muted-foreground">
              Correr el análisis con la agenda cargada.
            </span>
          </span>
        </label>
        <label className="flex cursor-pointer items-start gap-2 text-sm">
          <input
            type="radio"
            name="agenda"
            className="mt-0.5"
            checked={valor === false}
            onChange={() => onChange(false)}
          />
          <span>
            <span className="font-medium">Sí, hubo cambios.</span>{" "}
            <span className="text-muted-foreground">
              Hay que actualizar la agenda antes de correr.
            </span>
          </span>
        </label>
      </div>
      {valor === false && (
        <div className="mt-3 space-y-2 rounded-md border bg-card px-3 py-3">
          <div className="text-sm font-medium">Sube la agenda actualizada</div>
          <p className="text-xs text-muted-foreground">
            El concentrado de las tablas de escalación en CSV. Necesita al menos
            las columnas <code>agencia</code>, <code>nombre</code> y{" "}
            <code>telefono</code>; el resto (proyecto, nivel, puesto, empresa,
            correo) son opcionales. Reemplaza la agenda completa.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setArchivo(e.target.files?.[0] ?? null)}
              className="text-xs"
            />
            <button
              type="button"
              disabled={!archivo || subir.isPending}
              onClick={handleSubir}
              className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground disabled:opacity-50"
            >
              {subir.isPending ? "Subiendo…" : "Subir y continuar"}
            </button>
          </div>
          {subir.isError && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              No se pudo cargar el archivo. Revisa que traiga las columnas
              obligatorias y vuelve a intentar.
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            Mientras no se actualice, el análisis no corre: con la agenda vencida
            señalaría a contactos que ya no corresponden.
          </p>
        </div>
      )}

      {subir.isSuccess && valor === true && (
        <div className="mt-3 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs">
          Agenda actualizada: {subir.data.contactos} contactos de{" "}
          {subir.data.agencias} agencias.
          {subir.data.omitidas.length > 0 && (
            <>
              {" "}
              <b>{subir.data.omitidas.length} filas quedaron fuera</b> por no traer
              un teléfono marcable o faltarles agencia o nombre:
              <ul className="mt-1 list-inside list-disc">
                {subir.data.omitidas.slice(0, 5).map((o) => (
                  <li key={o}>{o}</li>
                ))}
              </ul>
              {subir.data.omitidas.length > 5 && (
                <span>y {subir.data.omitidas.length - 5} más.</span>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Step3Srp({
  area,
  selectedId,
  observations,
  agendaSinCambios,
  onSelect,
  onObservationsChange,
  onAgendaChange,
  onNext,
  onBack,
}: {
  area: EccArea;
  selectedId: string | null;
  observations: EccObservations | null;
  agendaSinCambios: boolean | null;
  onSelect: (id: string, obs: EccObservations) => void;
  onObservationsChange: (obs: EccObservations) => void;
  onAgendaChange: (v: boolean) => void;
  onNext: () => void;
  onBack: () => void;
}) {
  const { data, isPending, error } = useEccSrps(area);
  const official = (data?.items ?? []).filter((s) => s.status === "official");
  const selectedSrp = official.find((s) => s.id === selectedId) ?? null;
  // Con agenda pendiente de actualizar no se deja avanzar: el backend lo
  // rechaza igual, pero es mejor decirlo antes de llegar a la ejecución.
  const agendaBloquea =
    Boolean(selectedSrp?.requiere_agenda) && agendaSinCambios !== true;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Elige el SRP oficial a ejecutar para{" "}
        <strong>{area.toUpperCase()}</strong>.
      </p>

      {isPending && (
        <div className="space-y-2">
          {[1, 2].map((i) => (
            <div key={i} className="h-16 animate-pulse rounded-lg border bg-muted" />
          ))}
        </div>
      )}

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          Error al cargar el catálogo.
        </div>
      )}

      {!isPending && official.length === 0 && !error && (
        <div className="rounded-md border bg-card p-6 text-center text-sm text-muted-foreground">
          No hay SRPs oficiales para esta área. Publicá uno desde el Catálogo.
        </div>
      )}

      <div className="space-y-2">
        {official.map((srp) => (
          <button
            key={srp.id}
            type="button"
            onClick={() => onSelect(srp.id, _srpToDefaultObservations(srp))}
            className={cn(
              "w-full text-left rounded-lg border p-3 transition-colors",
              selectedId === srp.id
                ? "border-primary bg-primary/5"
                : "bg-card hover:bg-accent",
            )}
          >
            <div className="text-sm font-medium">{srp.name}</div>
            <div className="text-xs text-muted-foreground">{srp.description}</div>
            <div className="mt-1 text-[11px] text-muted-foreground">
              Formato: {srp.output_format.toUpperCase()}
            </div>
          </button>
        ))}
      </div>

      {selectedSrp?.requiere_agenda && (
        <AgendaCheck
          cliente={selectedSrp.agenda_cliente}
          valor={agendaSinCambios}
          onChange={onAgendaChange}
        />
      )}

      {selectedSrp?.transcription_only ? (
        <div className="rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
          Este SRP solo transcribe las llamadas del periodo (sin análisis). No
          hay puntos a analizar: se entrega un documento con la transcripción
          completa de cada llamada.
        </div>
      ) : (
        selectedSrp &&
        observations && (
          <ObservationsBlock
            presets={observations.presets}
            custom={observations.custom.map((c) => c.label)}
            onChange={onObservationsChange}
          />
        )
      )}

      <div className="flex justify-between">
        <button
          type="button"
          onClick={onBack}
          className="rounded-md border px-4 py-2 text-sm font-medium"
        >
          Atrás
        </button>
        <button
          type="button"
          disabled={!selectedId || agendaBloquea}
          onClick={onNext}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
        >
          Siguiente: ejecutar
        </button>
      </div>
    </div>
  );
}

// ── Step 4: Ejecución ────────────────────────────────────────────────────────

const RUN_STEPS = [
  { key: "fetching_cdr", label: "Extrayendo CDR de Asterisk" },
  { key: "downloading", label: "Descargando grabaciones" },
  { key: "transcribing", label: "Transcribiendo con AROC" },
  { key: "analyzing", label: "Analizando criterios de calidad" },
  { key: "generating", label: "Generando reporte" },
  { key: "done", label: "Completado" },
];

const STATUS_ORDER = [
  "pending",
  "fetching_cdr",
  "downloading",
  "transcribing",
  "analyzing",
  "generating",
  "done",
];

function Step4Execution({
  area,
  srpId,
  periodStart,
  periodEnd,
  observations,
  agendaSinCambios,
  onDone,
  onRunId,
}: {
  area: EccArea;
  srpId: string;
  periodStart: string;
  periodEnd: string;
  observations: EccObservations | null;
  agendaSinCambios: boolean | null;
  onDone: (runId: string) => void;
  onRunId: (id: string) => void;
}) {
  const [runId, setRunId] = useState<string | null>(null);
  const startRun = useEccStartRun();
  const { data: run } = useEccRun(runId);

  function handleStart() {
    startRun.mutate(
      {
        area,
        srp_id: srpId,
        period_start: periodStart,
        period_end: periodEnd,
        ...(observations ? { observations } : {}),
        ...(agendaSinCambios === null ? {} : { agenda_sin_cambios: agendaSinCambios }),
      },
      {
        onSuccess: (res) => {
          setRunId(res.run_id);
          onRunId(res.run_id);
        },
      },
    );
  }

  const currentStatus = run?.status ?? (startRun.isPending ? "pending" : null);
  const currentIdx = currentStatus ? STATUS_ORDER.indexOf(currentStatus) : -1;
  const failed = currentStatus === "failed";

  if (startRun.isPending && !runId) {
    return (
      <div className="py-8 text-center text-sm text-muted-foreground">
        Iniciando corrida…
      </div>
    );
  }

  if (!runId) {
    return (
      <div className="space-y-4">
        <div className="rounded-lg border bg-card p-4 space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Área</span>
            <span className="font-medium">{area.toUpperCase()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Periodo</span>
            <span className="font-medium">{periodStart} → {periodEnd}</span>
          </div>
        </div>

        {startRun.isError && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            No se pudo iniciar la corrida. Backend ECC no activo.
          </div>
        )}

        <button
          type="button"
          onClick={handleStart}
          className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
        >
          Iniciar análisis
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        {RUN_STEPS.map((s) => {
          const stepIdx = STATUS_ORDER.indexOf(s.key);
          const done = stepIdx < currentIdx || currentStatus === "done";
          const active = s.key === currentStatus;

          return (
            <div key={s.key} className="flex items-center gap-3">
              <div
                className={cn(
                  "size-5 rounded-full flex items-center justify-center shrink-0",
                  done
                    ? "bg-emerald-500"
                    : active
                      ? "bg-primary animate-pulse"
                      : "bg-muted",
                )}
              >
                {done && <CheckCircle2 className="size-3 text-white" />}
              </div>
              <span
                className={cn(
                  "text-sm",
                  done
                    ? "text-foreground"
                    : active
                      ? "font-medium text-foreground"
                      : "text-muted-foreground",
                )}
              >
                {s.label}
              </span>
              {active && !failed && (
                <span className="ml-auto text-xs text-muted-foreground animate-pulse">
                  en progreso…
                </span>
              )}
            </div>
          );
        })}
      </div>

      {run?.log_lines && run.log_lines.length > 0 && (
        <div className="rounded-md border bg-muted/30 p-3 max-h-36 overflow-y-auto font-mono text-[11px] text-muted-foreground space-y-0.5">
          {run.log_lines.map((line, i) => (
            <div key={i}>{line}</div>
          ))}
        </div>
      )}

      {failed && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {run?.error ?? "La corrida falló. Revisa los logs."}
        </div>
      )}

      {currentStatus === "done" && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => onDone(runId)}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
          >
            Ver resultado
          </button>
        </div>
      )}
    </div>
  );
}

// ── Step 5: Resultado ────────────────────────────────────────────────────────

function Step5Result({ runId, area }: { runId: string; area: EccArea }) {
  const { data: run } = useEccRun(runId);
  const navigate = useNavigate();
  const followup = useEccFollowup(runId);

  const isDone = run?.status === "done";
  const isRootRun = isDone && run?.parent_run_id === null;

  async function handleDrilldown(section: string) {
    await followup.mutateAsync({ kind: "drilldown", focus: { section } });
  }

  async function handleSecondary(focusText: string, observations: EccObservations) {
    await followup.mutateAsync({ kind: "secondary", focus: { focus_text: focusText }, observations });
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4">
        <CheckCircle2 className="size-6 shrink-0 text-emerald-600" />
        <div>
          <div className="text-sm font-medium">Análisis completado</div>
          <div className="text-xs text-muted-foreground">
            {run?.recordings_transcribed ?? "—"} grabaciones procesadas
          </div>
        </div>
      </div>

      {run?.output_file && (
        <a
          href={`/api/v1/ecc/runs/${runId}/download`}
          className="flex items-center gap-3 rounded-lg border bg-card p-4 transition-colors hover:bg-accent"
        >
          <Download className="size-5 text-primary" />
          <div>
            <div className="text-sm font-medium">Descargar reporte</div>
            <div className="text-xs text-muted-foreground">{run.output_file}</div>
          </div>
        </a>
      )}

      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={() => navigate(`/ecc/history?area=${area}`)}
          className="rounded-md border px-4 py-2 text-sm font-medium"
        >
          Ver historial
        </button>
        <button
          type="button"
          onClick={() => navigate(`/ecc/new?area=${area}`)}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
        >
          Nuevo análisis
        </button>
      </div>

      {isRootRun && run && (
        <FollowupPanel
          parentRun={run}
          onDrilldown={handleDrilldown}
          onSecondary={handleSecondary}
        />
      )}
    </div>
  );
}

// ── Wizard principal ─────────────────────────────────────────────────────────

export function EccNewAnalysisPage() {
  const { area } = useOutletContext<{ area: EccArea }>();
  const [searchParams] = useSearchParams();
  const preselectedSrp = searchParams.get("srp");

  const [step, setStep] = useState<Step>(1);
  const [periodStart, setPeriodStart] = useState(todayIso());
  const [periodEnd, setPeriodEnd] = useState(todayIso());
  const [selectedSrp, setSelectedSrp] = useState<string | null>(preselectedSrp);
  const [observations, setObservations] = useState<EccObservations | null>(null);
  const [agendaSinCambios, setAgendaSinCambios] = useState<boolean | null>(null);
  const [runId, setRunId] = useState<string | null>(null);

  return (
    <div className="max-w-2xl space-y-2">
      <StepIndicator current={step} />

      {step === 1 && (
        <Step1Period
          start={periodStart}
          end={periodEnd}
          onApply={(s, e) => {
            setPeriodStart(s);
            setPeriodEnd(e);
            setStep(2);
          }}
        />
      )}

      {step === 2 && (
        <Step2Cdr
          area={area}
          periodStart={periodStart}
          periodEnd={periodEnd}
          onNext={() => setStep(3)}
          onBack={() => setStep(1)}
        />
      )}

      {step === 3 && (
        <Step3Srp
          area={area}
          selectedId={selectedSrp}
          observations={observations}
          agendaSinCambios={agendaSinCambios}
          onSelect={(id, obs) => {
            setSelectedSrp(id);
            setObservations(obs);
            setAgendaSinCambios(null);
          }}
          onObservationsChange={setObservations}
          onAgendaChange={setAgendaSinCambios}
          onNext={() => setStep(4)}
          onBack={() => setStep(2)}
        />
      )}

      {step === 4 && selectedSrp && (
        <Step4Execution
          area={area}
          srpId={selectedSrp}
          periodStart={periodStart}
          periodEnd={periodEnd}
          observations={observations}
          agendaSinCambios={agendaSinCambios}
          onRunId={setRunId}
          onDone={(id) => {
            setRunId(id);
            setStep(5);
          }}
        />
      )}

      {step === 5 && runId && <Step5Result runId={runId} area={area} />}
    </div>
  );
}
